/**
 * Personal Muhurta Advisor Widget - Phase 2
 * Finds auspicious time windows with natal chart + transit + dasha integration
 */

import React, { useState, useMemo } from 'react';
import AsyncSelect from 'react-select/async';
import {
  Box,
  Typography,
  Select,
  Option,
  Button,
  Card,
  Chip,
  LinearProgress,
  Sheet,
  Divider,
  Alert,
  IconButton,
  Tooltip,
  Switch,
  FormControl,
  FormLabel,
} from '@mui/joy';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import BlockIcon from '@mui/icons-material/Block';
import * as Astronomy from 'astronomy-engine';
import {
  ActivityType,
  calculateEnhancedMuhurtaScore,
  MuhurtaFactors,
  NatalChartScoreInput,
  TransitScoreInput,
} from '../../astro/muhurta/muhurtaEngine';
import {
  searchCitiesSmart,
  type CityOption,
  POPULAR_CITY_OPTIONS,
} from '../../utils/cityUtils';

interface PersonalMuhurtaAdvisorProps {
  astroData: any;
  d1HouseScores?: Record<number, number> | null;
  bhavaBalaRupas?: Record<number, number> | null;
  dashaContext?: any;
  selectedCity?: any;
}

interface EnhancedTimeWindow {
  date: Date;
  score: number;
  factors: MuhurtaFactors;
  breakdown: Record<string, number>;
  natalScore?: number;
  transitScore?: number;
  blockers: string[];
  recommendations: string[];
  dateString: string;
  timeString: string;
}

const ACTIVITY_OPTIONS: { value: ActivityType; label: string; description: string }[] = [
  {
    value: 'business_start',
    label: '🏢 Start Business',
    description: 'Launching a new business venture or company registration',
  },
  {
    value: 'property_purchase',
    label: '🏡 Buy Property',
    description: 'Purchasing house, land, or real estate',
  },
  {
    value: 'graha_pravesh',
    label: '🚪 Graha Pravesh',
    description: 'Housewarming ceremony for new home',
  },
  {
    value: 'vehicle_purchase',
    label: '🚗 Buy Vehicle',
    description: 'Purchasing car, bike, or other vehicle',
  },
  {
    value: 'surgery',
    label: '🏥 Medical Surgery',
    description: 'Elective surgical procedures (emergency excluded)',
  },
  {
    value: 'ivf_planning',
    label: '👶 IVF Planning',
    description: 'Planning IVF/fertility treatment procedures',
  },
  {
    value: 'cesarean',
    label: '🤱 Cesarean Birth',
    description: 'Planned C-section delivery (choose birth time for child)',
  },
];

export default function PersonalMuhurtaAdvisor({
  astroData,
  d1HouseScores,
  bhavaBalaRupas,
  dashaContext,
  selectedCity,
}: PersonalMuhurtaAdvisorProps) {
  const [selectedActivity, setSelectedActivity] = useState<ActivityType>('business_start');
  const [scanning, setScanning] = useState(false);
  const [timeWindows, setTimeWindows] = useState<EnhancedTimeWindow[]>([]);
  const [daysToScan] = useState(60);
  const [useEnhancedMode, setUseEnhancedMode] = useState(true);
  const [muhurtaCity, setMuhurtaCity] = useState<CityOption | null>(null);
  const [showCitySearch, setShowCitySearch] = useState(false);

  const selectedActivityInfo = ACTIVITY_OPTIONS.find((a) => a.value === selectedActivity);

  // Update muhurta city when selectedCity changes
  React.useEffect(() => {
    if (selectedCity && !muhurtaCity) {
      setMuhurtaCity(selectedCity);
    }
  }, [selectedCity, muhurtaCity]);

  // Clear scan results when birth profile changes (astroData changes)
  // This forces re-scan with new natal chart context (different ascendant = different transit houses)
  React.useEffect(() => {
    if (timeWindows.length > 0) {
      setTimeWindows([]);
    }
  }, [astroData]);

  // Determine if enhanced mode is available
  const canUseEnhanced = useMemo(() => {
    return !!(astroData && (d1HouseScores || bhavaBalaRupas));
  }, [astroData, d1HouseScores, bhavaBalaRupas]);

  // Extract natal chart data
  const getNatalChartInput = (): NatalChartScoreInput | undefined => {
    if (!useEnhancedMode || !canUseEnhanced) return undefined;

    const houseStrengths = bhavaBalaRupas || d1HouseScores || undefined;

    let planetaryPeriods;
    if (dashaContext?.currentDashas) {
      const { MD, AD, PD, SD } = dashaContext.currentDashas;
      planetaryPeriods = { MD, AD, PD, SD };
    }

    return {
      activity: selectedActivity,
      houseStrengths,
      planetaryPeriods,
    };
  };

  // Get transit data for a specific date
  const getTransitInput = (date: Date): TransitScoreInput | undefined => {
    if (!useEnhancedMode || !canUseEnhanced || !astroData) return undefined;

    const ayanamsa = 24.14;
    const time = Astronomy.MakeTime(date);

    const transitPlanets: Record<string, { house: number; sign: number }> = {};
    const planets = ['Jupiter', 'Saturn', 'Mars', 'Venus', 'Mercury'];

    // Extract lagna sign from astroData (1-12) - use correct property path
    const lagnaSign = astroData.asc?.ascendantSignIndex || 1;

    planets.forEach((planetName) => {
      const body = Astronomy.Body[planetName as keyof typeof Astronomy.Body];
      if (!body) return;

      const vec = Astronomy.GeoVector(body, time, true);
      const ecl = Astronomy.Ecliptic(vec);
      const siderealLon = ((ecl.elon - ayanamsa) % 360 + 360) % 360;
      const sign = Math.floor(siderealLon / 30) + 1;

      // Calculate house using whole-sign system
      // Offset 0-11, then add 1 to get houses 1-12
      const offset = (sign - lagnaSign + 12) % 12;
      const house = offset + 1;

      transitPlanets[planetName] = { house, sign };
    });

    // Natal planets
    const natalPlanets: Record<string, { house: number; sign: number }> = {};
    if (astroData.planets) {
      Object.entries(astroData.planets).forEach(([name, data]: [string, any]) => {
        if (planets.includes(name)) {
          natalPlanets[name] = {
            house: data.house || 1,
            sign: data.sign || 1,
          };
        }
      });
    }

    return {
      activity: selectedActivity,
      transitPlanets,
      natalPlanets,
    };
  };

  // Scan for good time windows
  const scanTimeWindows = () => {
    if (!muhurtaCity) {
      alert('Please select a city first. Muhurta calculations depend on location (sunrise/sunset times).');
      return;
    }

    setScanning(true);
    setTimeWindows([]);

    setTimeout(() => {
      const windows: EnhancedTimeWindow[] = [];
      const today = new Date();
      const ayanamsa = 24.14;
      const lat = muhurtaCity.lat;
      const lon = muhurtaCity.lon;

      const natalInput = getNatalChartInput();

      for (let day = 0; day < daysToScan; day++) {
        const date = new Date(today);
        date.setDate(today.getDate() + day);
        date.setHours(10, 0, 0, 0);

        // Calculate Moon's sidereal longitude
        const time = Astronomy.MakeTime(date);
        const moonVec = Astronomy.GeoVector(Astronomy.Body.Moon, time, true);
        const moonEcl = Astronomy.Ecliptic(moonVec);
        const siderealLon = ((moonEcl.elon - ayanamsa) % 360 + 360) % 360;

        // Get transit input for this date
        const transitInput = getTransitInput(date);

        // Calculate enhanced muhurta score
        const result = calculateEnhancedMuhurtaScore(
          date,
          siderealLon,
          selectedActivity,
          natalInput,
          transitInput,
          lat,
          lon
        );

        // Filter: Include if score >= 40 OR if score is 0 with blockers (to show why blocked)
        if (result.score >= 40 || (result.score === 0 && result.blockers.length > 0)) {
          windows.push({
            date,
            score: result.score,
            factors: result.factors,
            breakdown: result.breakdown,
            natalScore: result.natalScore,
            transitScore: result.transitScore,
            blockers: result.blockers,
            recommendations: result.recommendations,
            dateString: date.toLocaleDateString('en-US', {
              weekday: 'long',
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            }),
            timeString: date.toLocaleTimeString('en-US', {
              hour: '2-digit',
              minute: '2-digit',
            }),
          });
        }
      }

      // Sort: Blocked (score 0) at bottom, then by score descending
      windows.sort((a, b) => {
        if (a.score === 0 && b.score > 0) return 1;
        if (b.score === 0 && a.score > 0) return -1;
        return b.score - a.score;
      });

      setTimeWindows(windows);
      setScanning(false);
    }, 800);
  };

  const getScoreColor = (score: number): string => {
    if (score === 0) return 'danger';
    if (score >= 75) return 'success';
    if (score >= 60) return 'primary';
    if (score >= 40) return 'warning';
    return 'neutral';
  };

  const getScoreLabel = (score: number): string => {
    if (score === 0) return 'Blocked';
    if (score >= 75) return 'Excellent';
    if (score >= 60) return 'Good';
    if (score >= 40) return 'Fair';
    return 'Poor';
  };

  return (
    <Box>
      {/* Description */}
      <Typography level="body-sm" sx={{ color: 'text.secondary', mb: 2 }}>
        Find favorable time windows using Muhurta principles + your natal chart + current transits.
      </Typography>

      {/* Enhanced Mode Toggle */}
      {canUseEnhanced && (
        <Card variant="outlined" sx={{ mb: 2, p: { xs: 1.5, sm: 2 } }}>
          <FormControl>
            <Box sx={{ 
              display: 'flex', 
              flexDirection: { xs: 'column', sm: 'row' },
              justifyContent: 'space-between', 
              alignItems: { xs: 'flex-start', sm: 'center' },
              gap: { xs: 1, sm: 0 }
            }}>
              <Box sx={{ flex: 1 }}>
                <FormLabel sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}>Enhanced Analysis Mode</FormLabel>
                <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                  Includes natal house strengths, dasha periods, and transit analysis
                </Typography>
              </Box>
              <Switch
                checked={useEnhancedMode}
                onChange={(e) => setUseEnhancedMode(e.target.checked)}
                color="primary"
                sx={{ alignSelf: { xs: 'flex-end', sm: 'center' } }}
              />
            </Box>
          </FormControl>
        </Card>
      )}

      {/* Location Selection - CRITICAL for Muhurta */}
      <Card variant="outlined" sx={{ mb: 2, p: { xs: 1.5, sm: 2 }, borderLeft: '4px solid var(--joy-palette-primary-500)' }}>
        <Typography level="title-md" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1, fontSize: { xs: '0.95rem', sm: '1.05rem' } }}>
          📍 Location
          <Tooltip title="Rahu Kaal and sunrise/sunset times vary by location">
            <IconButton size="sm" variant="plain">
              <InfoOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Typography>
        <Typography level="body-xs" sx={{ color: 'text.secondary', mb: 2 }}>
          Muhurta timings (especially Rahu Kaal) depend on sunrise/sunset, which varies by city.
        </Typography>
        
        {muhurtaCity && !showCitySearch ? (
          <Box>
            <Sheet variant="soft" sx={{ p: 1.5, borderRadius: 'sm', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography level="body-sm" sx={{ fontWeight: 600 }}>
                  {muhurtaCity.label}
                </Typography>
                <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                  {muhurtaCity.lat?.toFixed(2)}°N, {muhurtaCity.lon?.toFixed(2)}°E
                  {muhurtaCity.timezone && ` • ${muhurtaCity.timezone}`}
                </Typography>
              </Box>
              <Button
                size="sm"
                variant="outlined"
                onClick={() => setShowCitySearch(true)}
              >
                Change
              </Button>
            </Sheet>
          </Box>
        ) : (
          <Box>
            <Typography level="body-xs" sx={{ mb: 1, color: 'text.secondary' }}>
              Search for city (uses same database as birth details):
            </Typography>
            <AsyncSelect
              cacheOptions
              defaultOptions={POPULAR_CITY_OPTIONS}
              loadOptions={(input: string) => searchCitiesSmart(input)}
              onChange={(option) => {
                setMuhurtaCity(option);
                setShowCitySearch(false);
              }}
              placeholder="Type city name..."
              value={muhurtaCity}
              styles={{
                control: (base) => ({
                  ...base,
                  minHeight: '40px',
                  borderRadius: '8px',
                  borderColor: 'var(--joy-palette-neutral-outlinedBorder)',
                }),
                menu: (base) => ({
                  ...base,
                  zIndex: 9999,
                }),
              }}
            />
            {muhurtaCity && (
              <Button
                size="sm"
                variant="plain"
                onClick={() => setShowCitySearch(false)}
                sx={{ mt: 1 }}
              >
                Cancel
              </Button>
            )}
          </Box>
        )}
      </Card>

      {/* Important Disclaimer */}
      <Alert variant="soft" color="warning" sx={{ mb: 2, p: { xs: 1.5, sm: 2 } }} startDecorator={<WarningAmberIcon />}>
        <Box>
          <Typography level="title-sm" sx={{ mb: 0.5, fontSize: { xs: '0.875rem', sm: '0.95rem' } }}>
            Guidance, Not Guarantees
          </Typography>
          <Typography level="body-sm" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
            This tool identifies time windows where astrological factors are more supportive. It
            reduces resistance but does not guarantee outcomes. Always consult a qualified astrologer
            for major life decisions, especially for childbirth (mother's chart is crucial).
          </Typography>
        </Box>
      </Alert>

      {/* Activity Selection */}
      <Card variant="outlined" sx={{ mb: 2, p: { xs: 1.5, sm: 2 } }}>
        <Typography level="title-md" sx={{ mb: 1.5, fontSize: { xs: '0.95rem', sm: '1.05rem' } }}>
          Select Activity
        </Typography>
        <Select
          value={selectedActivity}
          onChange={(_, value) => value && setSelectedActivity(value)}
          sx={{ mb: 1 }}
        >
          {ACTIVITY_OPTIONS.map((option) => (
            <Option key={option.value} value={option.value}>
              {option.label}
            </Option>
          ))}
        </Select>
        {selectedActivityInfo && (
          <Typography level="body-sm" sx={{ color: 'text.secondary', mt: 1 }}>
            {selectedActivityInfo.description}
          </Typography>
        )}
        
        {/* Special warning for cesarean */}
        {selectedActivity === 'cesarean' && (
          <Alert variant="soft" color="danger" sx={{ mt: 2 }} startDecorator={<WarningAmberIcon />}>
            <Typography level="body-sm">
              <strong>CRITICAL:</strong> For childbirth, mother's horoscope is most important. This tool
              checks for Gandanta (absolute danger zone) and Rahu Kaal. Consult an expert astrologer
              who can analyze mother's chart, choose auspicious lagna for baby, and ensure no malefic
              aspects.
            </Typography>
          </Alert>
        )}
      </Card>

      {/* Scan Button */}
      <Button
        fullWidth
        size="lg"
        color="primary"
        onClick={scanTimeWindows}
        disabled={scanning || !muhurtaCity}
        startDecorator={<CalendarTodayIcon />}
        sx={{ mb: 2, fontSize: { xs: '0.875rem', sm: '1rem' }, py: { xs: 1.5, sm: 2 } }}
      >
        {scanning
          ? 'Scanning...'
          : !muhurtaCity
          ? 'Select City to Scan'
          : `${useEnhancedMode && canUseEnhanced ? 'Enhanced' : 'Basic'} Scan (${daysToScan} Days)`}
      </Button>

      {!muhurtaCity && (
        <Alert variant="soft" color="neutral" sx={{ mb: 3 }}>
          <Typography level="body-sm">
            💡 <strong>Why location matters:</strong> Rahu Kaal timing is calculated based on sunrise and sunset,
            which varies by latitude and longitude. Using the wrong location can give incorrect Muhurta timings.
          </Typography>
        </Alert>
      )}

      {/* Loading */}
      {scanning && <LinearProgress sx={{ mb: 3 }} />}

      {/* Results */}
      {timeWindows.length > 0 && (
        <Box>
          <Typography level="title-lg" sx={{ mb: 1.5, fontSize: { xs: '1.05rem', sm: '1.25rem' } }}>
            Found {timeWindows.filter((w) => w.score > 0).length} Favorable Windows
            {timeWindows.filter((w) => w.score === 0).length > 0 &&
              ` (${timeWindows.filter((w) => w.score === 0).length} blocked)`}
          </Typography>

          {timeWindows.slice(0, 15).map((window, idx) => (
            <Card
              key={idx}
              variant="outlined"
              sx={{
                mb: 1.5,
                p: { xs: 1.5, sm: 2 },
                borderLeft: `4px solid var(--joy-palette-${getScoreColor(window.score)}-500)`,
                opacity: window.score === 0 ? 0.6 : 1,
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: 'flex-start', gap: { xs: 1.5, sm: 2 } }}>
                {/* Score Badge */}
                <Box
                  sx={{
                    minWidth: { xs: '100%', sm: 60 },
                    textAlign: 'center',
                    display: 'flex',
                    flexDirection: { xs: 'row', sm: 'column' },
                    justifyContent: { xs: 'space-between', sm: 'flex-start' },
                    alignItems: 'center',
                    gap: { xs: 2, sm: 0.5 },
                  }}
                >
                  {window.score > 0 ? (
                    <>
                      <Typography level="h2" sx={{ color: `${getScoreColor(window.score)}.500` }}>
                        {window.score}
                      </Typography>
                      <Chip size="sm" color={getScoreColor(window.score) as any} variant="soft">
                        {getScoreLabel(window.score)}
                      </Chip>
                    </>
                  ) : (
                    <>
                      <BlockIcon sx={{ fontSize: '2.5rem', color: 'danger.500' }} />
                      <Chip size="sm" color="danger" variant="solid">
                        BLOCKED
                      </Chip>
                    </>
                  )}
                </Box>

                <Divider orientation="vertical" sx={{ display: { xs: 'none', sm: 'block' } }} />

                {/* Details */}
                <Box sx={{ flex: 1 }}>
                  <Typography level="title-md" sx={{ mb: 0.5 }}>
                    {window.dateString} at {window.timeString}
                  </Typography>

                  {/* Blockers - Show prominently */}
                  {window.blockers.length > 0 && (
                    <Alert variant="solid" color="danger" sx={{ mb: 1.5, p: 1 }}>
                      <Typography level="body-sm" component="div">
                        {window.blockers.map((blocker, i) => (
                          <div key={i}>{blocker}</div>
                        ))}
                      </Typography>
                    </Alert>
                  )}

                  {/* Muhurta Factors */}
                  <Box sx={{ display: 'flex', gap: { xs: 0.5, sm: 1 }, flexWrap: 'wrap', mb: 1 }}>
                    <Chip size="sm" variant="soft">
                      {window.factors.dayOfWeek}
                    </Chip>
                    <Chip size="sm" variant="soft">
                      {window.factors.tithi.name} ({window.factors.tithi.paksha})
                    </Chip>
                    <Chip size="sm" variant="soft">
                      {window.factors.nakshatraName}
                    </Chip>
                    <Chip size="sm" variant="soft">
                      {window.factors.isWaxingMoon ? '🌒 Waxing' : '🌘 Waning'}
                    </Chip>
                    {window.factors.isGandanta && (
                      <Chip size="sm" color="danger" variant="solid">
                        ⚠️ Gandanta
                      </Chip>
                    )}
                    {window.factors.isRahuKaal && (
                      <Chip size="sm" color="danger" variant="solid">
                        ⚠️ Rahu Kaal
                      </Chip>
                    )}
                  </Box>

                  {/* Enhanced Scores */}
                  {useEnhancedMode && canUseEnhanced && window.score > 0 && (
                    <Sheet variant="soft" sx={{ p: 1.5, borderRadius: 'sm', mb: 1.5 }}>
                      <Typography level="body-xs" sx={{ mb: 1, fontWeight: 600 }}>
                        Component Scores:
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', fontSize: '0.75rem' }}>
                        <Box>
                          Muhurta: <strong>{window.breakdown.nakshatra}</strong>
                        </Box>
                        {window.natalScore !== undefined && (
                          <Box>
                            Natal: <strong>{window.natalScore}</strong>
                          </Box>
                        )}
                        {window.transitScore !== undefined && (
                          <Box>
                            Transit: <strong>{window.transitScore}</strong>
                          </Box>
                        )}
                      </Box>
                    </Sheet>
                  )}

                  {/* Recommendations - Categorized Display */}
                  {window.recommendations.length > 0 && window.score > 0 && (
                    <Sheet variant="soft" sx={{ p: 1.5, borderRadius: 'sm', bgcolor: 'success.50' }}>
                      <Typography level="body-xs" sx={{ mb: 1, fontWeight: 600 }}>
                        ✓ Positive Factors:
                      </Typography>
                      
                      {/* Basic Muhurta Factors */}
                      {(() => {
                        const muhurtaFactors = window.recommendations.filter(r => 
                          r.includes('Nakshatra') || r.includes('Tithi') || r.includes('day') || r.includes('Moon phase')
                        );
                        if (muhurtaFactors.length === 0) return null;
                        return (
                          <Box sx={{ mb: 1.5 }}>
                            <Typography level="body-xs" sx={{ fontWeight: 600, color: 'neutral.700', mb: 0.5 }}>
                              Muhurta (Traditional):
                            </Typography>
                            {muhurtaFactors.map((rec, i) => (
                              <Typography key={i} level="body-xs" sx={{ ml: 1, color: 'success.700' }}>
                                • {rec}
                              </Typography>
                            ))}
                          </Box>
                        );
                      })()}

                      {/* Natal Chart Factors */}
                      {(() => {
                        const natalFactors = window.recommendations.filter(r => 
                          r.includes('House') || r.includes('dasha') || r.includes('lord') || r.includes('strength')
                        );
                        if (natalFactors.length === 0) return null;
                        return (
                          <Box sx={{ mb: 1.5 }}>
                            <Typography level="body-xs" sx={{ fontWeight: 600, color: 'neutral.700', mb: 0.5 }}>
                              Natal Chart Analysis:
                            </Typography>
                            {natalFactors.map((rec, i) => (
                              <Typography key={i} level="body-xs" sx={{ ml: 1, color: 'success.700' }}>
                                • {rec}
                              </Typography>
                            ))}
                          </Box>
                        );
                      })()}

                      {/* Transit Factors */}
                      {(() => {
                        const transitFactors = window.recommendations.filter(r => 
                          r.includes('Jupiter') || r.includes('Saturn') || r.includes('Mars') || 
                          r.includes('Venus') || r.includes('Mercury') || r.includes('transiting') || r.includes('aspects')
                        );
                        if (transitFactors.length === 0) return null;
                        return (
                          <Box>
                            <Typography level="body-xs" sx={{ fontWeight: 600, color: 'neutral.700', mb: 0.5 }}>
                              Current Transits:
                            </Typography>
                            {transitFactors.map((rec, i) => (
                              <Typography key={i} level="body-xs" sx={{ ml: 1, color: 'success.700' }}>
                                • {rec}
                              </Typography>
                            ))}
                          </Box>
                        );
                      })()}
                    </Sheet>
                  )}
                </Box>
              </Box>
            </Card>
          ))}

          {timeWindows.length > 15 && (
            <Typography level="body-sm" sx={{ textAlign: 'center', color: 'text.secondary', mt: 2 }}>
              Showing top 15 results. Total {timeWindows.length} windows analyzed.
            </Typography>
          )}
        </Box>
      )}

      {/* No Results */}
      {!scanning && timeWindows.length === 0 && (
        <Alert variant="soft" color="neutral" sx={{ textAlign: 'center' }}>
          <Typography level="body-md">
            Click scan button to find auspicious time windows.
          </Typography>
        </Alert>
      )}

      {/* Educational Info */}
      <Card variant="soft" sx={{ mt: 2, p: { xs: 1.5, sm: 2 } }}>
        <Typography level="title-sm" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1, fontSize: { xs: '0.875rem', sm: '0.95rem' } }}>
          <InfoOutlinedIcon fontSize="small" />
          Key Features
        </Typography>
        <Typography level="body-sm" component="div" sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
          <ul style={{ margin: 0, paddingLeft: '1.2rem' }}>
            <li>
              <strong>Location-Based Rahu Kaal:</strong> Calculates inauspicious time periods based on
              actual sunrise/sunset for your selected city (varies by latitude/longitude)
            </li>
            <li>
              <strong>Gandanta Detection:</strong> Critical Moon positions (junction zones) - absolutely
              avoided for childbirth
            </li>
            <li>
              <strong>Natal Chart Integration:</strong> Analyzes relevant house strengths for the
              activity (10th for business, 4th for property, 5th for childbirth)
            </li>
            <li>
              <strong>Transit Analysis:</strong> Checks Jupiter, Saturn, Mars, Venus, Mercury transits
              to relevant houses
            </li>
            <li>
              <strong>Dasha Compatibility:</strong> Considers if current planetary periods are favorable
              for the activity
            </li>
          </ul>
        </Typography>
        <Divider sx={{ my: 1.5 }} />
        <Typography level="body-xs" sx={{ color: 'text.tertiary', fontStyle: 'italic' }}>
          <strong>Mother's Chart for Childbirth:</strong> For cesarean delivery, ideally use mother's
          birth chart for this analysis. The tool can also help choose an auspicious lagna (ascendant)
          for the baby's chart by scanning different hours of the selected day.
        </Typography>
      </Card>
    </Box>
  );
}
